import React from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ActivityIcon, CalendarClockIcon, ChevronRightIcon, FileTextIcon, HelpCircleIcon, KeyRoundIcon, ScrollTextIcon } from 'lucide-react';
import { loanOfficer, processor } from '../../data/team';
import type { BorrowerTask, TaskKind } from '../../types/application';
import { AnimatedCheck } from '../ui/AnimatedCheck';
import { useTokens } from '../../hooks/useTokens';
const kindIcon: Record<TaskKind, React.ReactNode> = {
  document: <FileTextIcon className="h-4 w-4" data-id="element-503" data-mp="3kfxiq6hnicqr" />,
  question: <HelpCircleIcon className="h-4 w-4" data-id="element-504" data-mp="2akjqn3ir9fd" />,
  appraisal: <KeyRoundIcon className="h-4 w-4" data-id="element-505" data-mp="2a1di3n8e9h6" />,
  title: <ScrollTextIcon className="h-4 w-4" data-id="element-506" data-mp="3thdlpt64zcky" />
};
function Requester({
  from
}: {
  from: BorrowerTask['from'];
}) {
  if (from === 'pulse') return <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-pulse-600 text-white" aria-label="Pulse" data-id="element-507" data-mp="242phif7p90y0">
        <ActivityIcon className="h-5 w-5" aria-hidden data-id="element-508" data-mp="1z5gyydin2uw8" />
      </span>;
  const p = from === 'processor' ? processor : loanOfficer;
  return <img src={p.photo} alt={p.name} className="h-10 w-10 shrink-0 rounded-full object-cover" data-id="element-509" data-mp="ngyibwti92ke" />;
}
export function TaskList({
  tasks
}: {
  tasks: BorrowerTask[];
}) {
  const open = tasks.filter(t => !t.done);
  const {
    fill
  } = useTokens();
  if (open.length === 0) {
    return <div className="flex flex-col items-center rounded-3xl bg-white px-6 py-12 text-center ring-1 ring-ink-200" data-id="element-510" data-mp="119emx22218nf">
        <AnimatedCheck size={52} data-id="element-511" data-mp="167g8puqv7m2l" />
        <p className="mt-5 text-lg font-semibold text-ink-900" data-id="element-512" data-mp="166odil16jqcu">You’re all caught up</p>
        <p className="mt-1 max-w-xs text-sm text-ink-500" data-id="element-513" data-mp="1b8nejo9g6b6m">We’ll text you the moment we need anything else.</p>
      </div>;
  }
  return <ul className="divide-y divide-ink-100 overflow-hidden rounded-3xl bg-white ring-1 ring-ink-200" data-id="element-514" data-mp="1g42oi54c3mxr">
      <AnimatePresence initial={false} data-id="element-515" data-mp="3hxsa740bpz5a">
        {open.map((t, i) => <motion.li key={t.id} layout exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.24,
        ease: [0.23, 1, 0.32, 1]
      }} data-id="element-516" data-mp="3hy20ytt70ac1" data-mpi={i}>
            <Link to={`/loan/task/${t.id}`} className="group flex items-center gap-4 p-4 transition-colors duration-150 hover:bg-ink-50 md:p-5" data-id="element-517" data-mp="34y9c50wt3e8p">
              <Requester from={t.from} data-id="element-518" data-mp="3jw1ppnislsk9" />
              <div className="min-w-0 flex-1" data-id="element-519" data-mp="3jsxhmf27icob">
                <p className={`font-medium text-ink-900 ${i === 0 ? 'md:text-[17px]' : ''}`} data-id="element-520" data-mp="3ev5qhn9dyul6">{fill(t.title)}</p>
                <p className="mt-0.5 line-clamp-2 text-sm text-ink-500" data-id="element-521" data-mp="3sudpaf1n00ou">{fill(t.summary)}</p>
                <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-ink-500" data-id="element-522" data-mp="3nw3uw6nql44a">
                  <span className="inline-flex items-center gap-1" data-id="element-523" data-mp="6f2fb7j2ens3">
                    {kindIcon[t.kind]}
                    {t.kind === 'document' ? 'Upload' : t.kind === 'question' ? 'Quick answer' : t.kind === 'appraisal' ? 'Schedule' : 'Choose'}
                  </span>
                  {t.due && <span className="inline-flex items-center gap-1 font-medium text-caution-700" data-id="element-524" data-mp="bh1k8lugdsr5">
                      <CalendarClockIcon className="h-3.5 w-3.5" aria-hidden data-id="element-525" data-mp="u4aejklt2xgi" /> Due {t.due}
                    </span>}
                </div>
              </div>
              <ChevronRightIcon className="h-5 w-5 shrink-0 text-ink-300 transition-transform duration-150 group-hover:translate-x-0.5 group-hover:text-ink-500" aria-hidden data-id="element-526" data-mp="l3vt8l8udid0" />
            </Link>
          </motion.li>)}
      </AnimatePresence>
    </ul>;
}